import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@y42/environment';
import {
  DynamicOrchestrationConfig,
  OrchestrationGitLink,
  OrchestrationRunStatus,
  OrchestrationType,
  StaticOrchestrationConfig,
} from '@y42/json-schema';
import { ORG_API_URL_PARAM } from '@y42/model';
import { UserUIModel } from '@y42/user/models';
import { isNullish } from '@y42/util/nullish';
import { OrchestrationRunJobDetailsStoreModel } from '../../state';

export interface TriggerOrchestrationLocationPayload {
  location: string;
}

export type TriggerOrchestrationRunDynamicPayload = DynamicOrchestrationConfig;
export type TriggerOrchestrationRunStaticPayload = StaticOrchestrationConfig;

export type TriggerOrchestrationRunPayload =
  | TriggerOrchestrationLocationPayload
  | TriggerOrchestrationRunDynamicPayload
  | TriggerOrchestrationRunStaticPayload;

export const isLocationPayload = (
  config: TriggerOrchestrationRunPayload,
): config is TriggerOrchestrationLocationPayload => {
  return !isNullish((config as TriggerOrchestrationLocationPayload).location);
};

export const isDynamicPayload = (
  config: TriggerOrchestrationRunPayload,
): config is TriggerOrchestrationRunDynamicPayload => {
  return !isNullish((config as TriggerOrchestrationRunDynamicPayload).assets);
};

export const isStaticPayload = (
  config: TriggerOrchestrationRunPayload,
): config is TriggerOrchestrationRunStaticPayload => {
  return !isNullish((config as TriggerOrchestrationRunStaticPayload).nodeDataArray);
};

export interface OrchestrationRunHttpModel {
  run_id: string;
  type: OrchestrationType;
  status: OrchestrationRunStatus;
  payload: TriggerOrchestrationRunPayload;
  run_config: StaticOrchestrationConfig | DynamicOrchestrationConfig;
  triggered_by: {
    type: 'user' | 'scheduler';
    mode: 'adhoc' | 'scheduler';
    user_id?: UserUIModel['id'];
  };
  created_at: string;
  updated_at: string | null;
  finished_at: string | null;
  details: {
    links: OrchestrationGitLink[];
    nodes: Record<string, OrchestrationRunJobDetailsStoreModel>;
  };
}

export interface OrchestrationRunsPayload {
  onlyTriggeredByMe: boolean;
  from: Date | null;
  to: Date | null;
}

@Injectable({
  providedIn: 'root',
})
export class OrchestrationRunHttpService {
  constructor(private readonly http: HttpClient) {}

  private readonly url = `${environment.url.orchestration_api}/company/${ORG_API_URL_PARAM}`;

  triggerRun(config: TriggerOrchestrationRunPayload) {
    return this.http.post(this.url, { payload: config });
  }

  getRun(runId: OrchestrationRunHttpModel['run_id']) {
    return this.http.get<OrchestrationRunHttpModel>(`${this.url}/${runId}`);
  }

  stopRun(runId: OrchestrationRunHttpModel['run_id']) {
    return this.http.post(`${this.url}/${runId}/stop`, {});
  }

  getAllRuns(config?: OrchestrationRunsPayload) {
    const params = config
      ? {
          only_triggered_by_me: !!config.onlyTriggeredByMe,
          ...(config?.from ? { after: config.from.toISOString() } : {}),
          ...(config?.to ? { before: config.to.toISOString() } : {}),
        }
      : undefined;
    return this.http.get<OrchestrationRunHttpModel[]>(this.url, { params });
  }

  getRunsBatch(orchestrationLocations: string[], noOfRuns?: number) {
    const locationString = orchestrationLocations.map((x) => `${x}/*`).join(',');
    const headers = new HttpHeaders({
      'x-storage-location': locationString,
    });
    const params = new HttpParams({ fromObject: noOfRuns !== undefined ? { limit: noOfRuns } : {} });

    return this.http.get<Record<string, OrchestrationRunHttpModel[]>>(`${this.url}/grouped_by_locations`, {
      headers,
      params,
    });
  }
}
